import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";

import ProjectCard from "../components/ProjectCard";
import ProjectFilters from "../components/ProjectFilters";
import ProjectFormModal from "../components/ProjectFormModal";
import ProjectTable from "../components/ProjectTable";
import ProjectsHeader from "../../components/ProjectsHeader";
import Footer from "../../components/Footer";
import {
  createProject,
  deleteProject,
  fetchProjects,
  updateProject,
} from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectProjects,
  selectProjectsError,
  selectProjectsLoading,
} from "../../../../core/data/redux/projects/selectors";
import { errMsg } from "../../helpers/errMsg.helper";
import { CONFIRM_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";

import type {
  Project,
  ProjectFilters as ProjectFiltersType,
} from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

const INITIAL_FILTERS: ProjectFiltersType = {
  search: "",
  status: null,
  priority: null,
  page: 1,
  limit: 50,
};

const ProjectsList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const projects = useSelector(selectProjects);
  const loading = useSelector(selectProjectsLoading);
  const error = useSelector(selectProjectsError);
  const activeCompany = useSelector(selectActiveCompany);

  const [filters, setFilters] = useState<ProjectFiltersType>(INITIAL_FILTERS);
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [formOpen, setFormOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const filtersKey = useMemo(
    () => JSON.stringify(filters) + activeCompany.company_id + activeCompany.company_type,
    [filters, activeCompany]
  );

  useEffect(() => {
    if (activeCompany.company_id) {
      dispatch(fetchProjects(filters));
    }
  }, [filtersKey]);

  const handleFilterChange = useCallback(
    (partial: Partial<ProjectFiltersType>) => {
      setFilters((p) => ({ ...p, ...partial }));
    },
    []
  );

  const handleCreate = useCallback(() => {
    setEditingProject(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((project: Project) => {
    setEditingProject(project);
    setFormOpen(true);
  }, []);

  const handleDelete = useCallback(
    (project: Project) => {
      Modal.confirm({
        title: `Delete project '${project.name}'?`,
        content:
          "This will deactivate the project and all related issues, sprints, and boards. This cannot be undone from the UI.",
        okText: "Delete",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        ...CONFIRM_MODAL_PROPS,
        onOk: async () => {
          try {
            await dispatch(deleteProject(project.id)).unwrap();
            notify.success("Project deleted");
          } catch (e: any) {
            notify.error(errMsg(e, "Failed to delete project"));
          }
        },
      });
    },
    [dispatch]
  );

  const handleSubmit = useCallback(
    async (payload: Record<string, any>) => {
      setSubmitting(true);
      try {
        if (editingProject) {
          await dispatch(
            updateProject({ projectId: editingProject.id, payload })
          ).unwrap();
          notify.success("Project updated");
        } else {
          await dispatch(createProject(payload)).unwrap();
          notify.success("Project created");
          dispatch(fetchProjects(filters));
        }
        setFormOpen(false);
        setEditingProject(null);
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to save project"));
        throw e;
      } finally {
        setSubmitting(false);
      }
    },
    [dispatch, editingProject, filters]
  );

  const handleCancel = useCallback(() => {
    setFormOpen(false);
    setEditingProject(null);
  }, []);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <ProjectsHeader title="Projects" breadcrumb="All Projects" />
          <ProjectFilters
            filters={filters}
            viewMode={viewMode}
            onFilterChange={handleFilterChange}
            onViewModeChange={setViewMode}
            onCreateClick={handleCreate}
            totalCount={projects.length}
            isLoading={loading}
          />

          {error && (
            <div
              style={{
                margin: "0 0 16px",
                padding: "10px 14px",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                color: "#dc2626",
                fontSize: 13,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <i className="ti ti-alert-triangle" style={{ fontSize: 16 }} />
              {error}
            </div>
          )}

          {!activeCompany.company_id ? (
            <div
              className="d-flex justify-content-center align-items-center bg-white"
              style={{ minHeight: 300, borderRadius: 12, border: "1px solid #e2e8f0" }}
            >
              <div className="text-center">
                <i className="ti ti-building" style={{ fontSize: 48, color: "#cbd5e1" }} />
                <h5 className="mt-3 mb-2 fw-semibold">No Company Selected</h5>
                <p className="text-muted mb-0">
                  Select a company from the header to view projects.
                </p>
              </div>
            </div>
          ) : loading && projects.length === 0 ? (
            <div
              className="d-flex justify-content-center align-items-center bg-white"
              style={{ minHeight: 400, borderRadius: 12, border: "1px solid #e2e8f0" }}
            >
              <div className="text-center">
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}
                />
                <p className="mt-3 text-muted">Loading projects...</p>
              </div>
            </div>
          ) : projects.length === 0 ? (
            <div
              className="d-flex justify-content-center align-items-center bg-white"
              style={{ minHeight: 300, borderRadius: 12, border: "1px solid #e2e8f0" }}
            >
              <div className="text-center">
                <i className="ti ti-folder-off" style={{ fontSize: 48, color: "#cbd5e1" }} />
                <h5 className="mt-3 mb-2 fw-semibold">No Projects Yet</h5>
                <p className="text-muted mb-3">
                  Get started by creating your first project.
                </p>
                <button
                  type="button"
                  className="btn btn-info"
                  onClick={handleCreate}
                >
                  <i className="ti ti-plus me-1" />
                  Create your first project
                </button>
              </div>
            </div>
          ) : viewMode === "grid" ? (
            <div>
              <div className="row">
                {projects.map((project) => (
                  <div key={project.id} className="col-xxl-3 col-xl-4 col-md-6 mb-4">
                    <ProjectCard
                      project={project}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <ProjectTable
              projects={projects}
              loading={loading}
              filters={filters}
              totalCount={projects.length}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onFilterChange={handleFilterChange}
            />
          )}
        </div>
        <Footer />
      </div>

      <ProjectFormModal
        open={formOpen}
        initialProject={editingProject}
        submitting={submitting}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </>
  );
};

export default ProjectsList;
